import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DisputeDto, DisputeStatus } from "@shared/types/DisputeDto";

interface DisputeTableProps {
  disputes: DisputeDto[];
  onViewDetails: (dispute: DisputeDto) => void;
}

const getStatusColor = (status: DisputeStatus) => {
  switch (status) {
    case "OPEN":
      return "bg-blue-500/20 text-blue-400 border-blue-500/30";
    case "UNDER_REVIEW":
      return "bg-yellow-500/20 text-yellow-400 border-yellow-500/30";
    case "AWAITING_CLIENT_RESPONSE":
      return "bg-purple-500/20 text-purple-400 border-purple-500/30";
    case "IN_MEDIATION":
      return "bg-orange-500/20 text-orange-400 border-orange-500/30";
    case "RESOLVED":
      return "bg-emerald-500/20 text-emerald-400 border-emerald-500/30";
    case "CLOSED":
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    default:
      return "bg-gray-500/20 text-gray-400 border-gray-500/30";
  }
};

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "CRITICAL":
      return "bg-red-500/20 text-red-400";
    case "HIGH":
      return "bg-orange-500/20 text-orange-400";
    case "MEDIUM":
      return "bg-yellow-500/20 text-yellow-400";
    case "LOW":
      return "bg-blue-500/20 text-blue-400";
    default:
      return "bg-gray-500/20 text-gray-400";
  }
};

export function DisputeTable({ disputes, onViewDetails }: DisputeTableProps) {
  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-border/50">
              <TableHead>ID</TableHead>
              <TableHead>Project</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Priority</TableHead>
              <TableHead>Date Filed</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {disputes.map((dispute) => (
              <TableRow
                key={dispute.id}
                className="border-border/50 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => onViewDetails(dispute)}
              >
                <TableCell className="font-mono text-sm">
                  {dispute.id}
                </TableCell>
                <TableCell>
                  <div>
                    <p className="font-medium">{dispute.title}</p>
                    <p className="text-xs text-muted-foreground">
                      Project ID: {dispute.projectId}
                    </p>
                  </div>
                </TableCell>
                <TableCell>
                  <Badge variant="outline" className="border-border/50">
                    {dispute.category.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Badge className={getPriorityColor(dispute.priority)}>
                    {dispute.priority}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm">
                  {dispute.createdAt.toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Badge className={getStatusColor(dispute.status)}>
                    {dispute.status.replace(/_/g, " ")}
                  </Badge>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
